import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Search, BookOpen, MessageCircle, Send, Loader, 
  ChevronRight, ExternalLink, Sparkles, ArrowRight, Phone,
  Smile, Paperclip, AtSign, Users, Calendar, Mail, BarChart3,
  Settings, Zap, FileText, Globe, CreditCard, Bell, Shield,
  Megaphone, Target, PieChart, MessageSquare, Workflow, Database,
  Layers, CheckCircle, PlayCircle, Clock, Tag, Filter, Folder,
  HelpCircle, Lightbulb, Rocket, Star, TrendingUp, RefreshCw,
  GraduationCap, ShoppingCart, UserPlus, Briefcase
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { EnhancedChatInput } from '../components/EnhancedChatInput';
import { api } from '../lib/auth';
import { cn, formatAIResponse } from '../lib/utils';
import { toast } from 'sonner';
import { AthenaVoiceChat } from '../components/AthenaVoiceChat';

// Quick Links for Knowledge Base
const QUICK_LINKS = [
  { path: '/onboarding', label: 'Onboarding', icon: UserPlus, color: 'from-cyan-500 to-blue-500' },
  { path: '/training', label: 'My Training', icon: GraduationCap, color: 'from-purple-500 to-pink-500' },
  { path: '/affiliate-training', label: 'Affiliate Training', icon: TrendingUp, color: 'from-green-500 to-emerald-500' },
  { path: '/orders', label: 'My Orders', icon: ShoppingCart, color: 'from-orange-500 to-amber-500' },
];

// CRM Help Categories - White-labeled from GHL
const CRM_CATEGORIES = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: Rocket,
    color: 'from-cyan-500 to-blue-500',
    description: 'New to the platform? Start here!',
    articles: [
      { id: 'gs-1', title: 'Welcome to AI Horse-Powered CRM', content: 'Your all-in-one business automation platform. This guide will help you get started with managing contacts, automations, funnels, and more.' },
      { id: 'gs-2', title: 'Setting Up Your Account', content: 'Learn how to configure your account settings, add team members, and customize your dashboard for optimal workflow.' },
      { id: 'gs-3', title: 'Understanding the Dashboard', content: 'Navigate through your CRM dashboard to access contacts, conversations, calendars, opportunities, and reporting.' },
      { id: 'gs-4', title: 'Quick Start Checklist', content: '1. Complete your business profile\n2. Connect your phone and email\n3. Import your contacts\n4. Set up your first automation\n5. Create a booking calendar' },
      { id: 'gs-5', title: 'Mobile App Setup', content: 'Download the mobile app to manage your business on the go. Available for iOS and Android devices.' },
    ]
  },
  {
    id: 'contacts',
    title: 'Contacts & CRM',
    icon: Users,
    color: 'from-green-500 to-emerald-500',
    description: 'Manage your contacts and leads',
    articles: [
      { id: 'ct-1', title: 'Adding & Importing Contacts', content: 'Import contacts via CSV, manually add them, or capture them through forms and funnels. Bulk import supports up to 50,000 contacts at once.' },
      { id: 'ct-2', title: 'Contact Tags & Smart Lists', content: 'Organize contacts with tags and create smart lists based on behavior, tags, or custom fields for targeted marketing.' },
      { id: 'ct-3', title: 'Custom Fields', content: 'Create custom fields to store additional information about your contacts like preferences, purchase history, or any business-specific data.' },
      { id: 'ct-4', title: 'Contact Activity & Timeline', content: 'View complete contact history including emails, calls, texts, appointments, and all interactions in one timeline.' },
      { id: 'ct-5', title: 'Duplicate Management', content: 'Find and merge duplicate contacts to keep your database clean and accurate.' },
      { id: 'ct-6', title: 'Lead Scoring', content: 'Automatically score leads based on engagement, helping your sales team prioritize the hottest prospects.' },
    ]
  },
  {
    id: 'conversations',
    title: 'Conversations',
    icon: MessageSquare,
    color: 'from-purple-500 to-pink-500',
    description: 'Unified inbox for all communications',
    articles: [
      { id: 'cv-1', title: 'Unified Inbox Overview', content: 'Manage all your conversations in one place - SMS, email, Facebook Messenger, Instagram DM, Google Business Messages, and more.' },
      { id: 'cv-2', title: 'Two-Way SMS Messaging', content: 'Send and receive text messages directly from your CRM. Set up auto-replies and use templates for quick responses.' },
      { id: 'cv-3', title: 'Email Integration', content: 'Connect your email accounts to send and receive emails within the CRM. Track opens, clicks, and replies.' },
      { id: 'cv-4', title: 'Facebook & Instagram Messaging', content: 'Connect your social accounts to manage DMs and comments from one dashboard.' },
      { id: 'cv-5', title: 'Google Business Messages', content: 'Respond to Google Business Profile messages directly from your inbox.' },
      { id: 'cv-6', title: 'Conversation AI', content: 'Use AI to draft responses, summarize conversations, and automate initial contact responses.' },
    ]
  },
  {
    id: 'calendars',
    title: 'Calendars & Appointments',
    icon: Calendar,
    color: 'from-orange-500 to-amber-500',
    description: 'Schedule and manage bookings',
    articles: [
      { id: 'cal-1', title: 'Creating Booking Calendars', content: 'Set up different calendar types: Round Robin, Class Booking, Collective, or Service calendars for various booking needs.' },
      { id: 'cal-2', title: 'Availability Settings', content: 'Configure your working hours, buffer times between appointments, and block off personal time.' },
      { id: 'cal-3', title: 'Calendar Integrations', content: 'Sync with Google Calendar, Outlook, or iCal to prevent double-booking and keep all calendars updated.' },
      { id: 'cal-4', title: 'Appointment Reminders', content: 'Set up automatic email and SMS reminders to reduce no-shows. Customize timing and message content.' },
      { id: 'cal-5', title: 'Group & Class Bookings', content: 'Allow multiple people to book the same time slot for classes, webinars, or group sessions.' },
      { id: 'cal-6', title: 'Payment Integration', content: 'Collect payments at the time of booking for paid consultations or services.' },
    ]
  },
  {
    id: 'automations',
    title: 'Automations & Workflows',
    icon: Zap,
    color: 'from-yellow-500 to-orange-500',
    description: 'Automate your business processes',
    articles: [
      { id: 'auto-1', title: 'Workflow Builder Basics', content: 'Create powerful automations using our drag-and-drop workflow builder. Trigger actions based on contact behavior, time, or events.' },
      { id: 'auto-2', title: 'Triggers & Actions', content: 'Triggers start workflows (form submission, tag added, appointment booked). Actions are what happens next (send email, add tag, create task).' },
      { id: 'auto-3', title: 'If/Else Conditions', content: 'Add branching logic to your workflows based on contact data, behavior, or custom conditions.' },
      { id: 'auto-4', title: 'Wait Steps & Delays', content: 'Add time delays between actions - wait specific hours, days, or until a specific time of day.' },
      { id: 'auto-5', title: 'Workflow Recipes', content: 'Start with pre-built workflow templates for common use cases like lead nurturing, appointment reminders, and review requests.' },
      { id: 'auto-6', title: 'Testing Workflows', content: 'Test your workflows before activating to ensure everything works as expected.' },
    ]
  },
  {
    id: 'funnels',
    title: 'Funnels & Websites',
    icon: Layers,
    color: 'from-blue-500 to-indigo-500',
    description: 'Build landing pages and funnels',
    articles: [
      { id: 'fn-1', title: 'Funnel Builder Overview', content: 'Create high-converting landing pages and multi-step funnels with our drag-and-drop builder.' },
      { id: 'fn-2', title: 'Page Elements & Sections', content: 'Add text, images, videos, forms, buttons, countdown timers, and more to your pages.' },
      { id: 'fn-3', title: 'Forms & Surveys', content: 'Create lead capture forms and surveys. Map fields to contact records and trigger automations on submission.' },
      { id: 'fn-4', title: 'A/B Split Testing', content: 'Test different page versions to optimize conversion rates. Let data guide your decisions.' },
      { id: 'fn-5', title: 'Domain Setup', content: 'Connect your custom domain or use a subdomain for your funnels and websites.' },
      { id: 'fn-6', title: 'Membership Sites', content: 'Create gated content areas with courses, modules, and member-only access.' },
    ]
  },
  {
    id: 'opportunities',
    title: 'Opportunities & Pipelines',
    icon: Target,
    color: 'from-red-500 to-pink-500',
    description: 'Track deals and sales pipeline',
    articles: [
      { id: 'opp-1', title: 'Pipeline Setup', content: 'Create sales pipelines with custom stages to track deals from lead to close.' },
      { id: 'opp-2', title: 'Managing Opportunities', content: 'Add opportunities, assign values, set close dates, and move them through pipeline stages.' },
      { id: 'opp-3', title: 'Pipeline Automations', content: 'Trigger workflows when opportunities move stages, are won, or are lost.' },
      { id: 'opp-4', title: 'Sales Reporting', content: 'Track pipeline value, conversion rates, and sales team performance with built-in reports.' },
      { id: 'opp-5', title: 'Tasks & Follow-ups', content: 'Create tasks linked to opportunities to ensure timely follow-ups.' },
    ]
  },
  {
    id: 'marketing',
    title: 'Email & SMS Marketing',
    icon: Mail,
    color: 'from-teal-500 to-cyan-500',
    description: 'Campaigns and broadcasts',
    articles: [
      { id: 'mkt-1', title: 'Email Campaigns', content: 'Create and send email campaigns to your contact lists. Use templates or build from scratch.' },
      { id: 'mkt-2', title: 'SMS Campaigns', content: 'Send bulk SMS messages for promotions, announcements, or reminders. Track delivery and responses.' },
      { id: 'mkt-3', title: 'Email Templates', content: 'Design reusable email templates with our drag-and-drop editor. Include dynamic personalization.' },
      { id: 'mkt-4', title: 'Campaign Analytics', content: 'Track open rates, click rates, bounces, and unsubscribes for all your campaigns.' },
      { id: 'mkt-5', title: 'List Segmentation', content: 'Target specific contact segments for more relevant and effective marketing.' },
    ]
  },
  {
    id: 'reporting',
    title: 'Reporting & Analytics',
    icon: BarChart3,
    color: 'from-indigo-500 to-purple-500',
    description: 'Track performance and insights',
    articles: [
      { id: 'rpt-1', title: 'Dashboard Overview', content: 'Your main dashboard shows key metrics: leads, appointments, pipeline value, and campaign performance.' },
      { id: 'rpt-2', title: 'Attribution Reports', content: 'Track which sources, campaigns, and channels are driving the most leads and revenue.' },
      { id: 'rpt-3', title: 'Call Reporting', content: 'Analyze call volume, duration, outcomes, and recordings for your team.' },
      { id: 'rpt-4', title: 'Custom Reports', content: 'Build custom reports with the data points that matter most to your business.' },
      { id: 'rpt-5', title: 'Exporting Data', content: 'Export reports to CSV for further analysis or sharing with stakeholders.' },
    ]
  },
  {
    id: 'integrations',
    title: 'Integrations',
    icon: Workflow,
    color: 'from-pink-500 to-rose-500',
    description: 'Connect with other tools',
    articles: [
      { id: 'int-1', title: 'Available Integrations', content: 'Connect with Stripe, PayPal, Quickbooks, Zapier, Facebook Ads, Google Ads, and hundreds more.' },
      { id: 'int-2', title: 'Zapier Integration', content: 'Use Zapier to connect your CRM with 5000+ apps for endless automation possibilities.' },
      { id: 'int-3', title: 'Facebook & Google Ads', content: 'Import leads directly from your ad campaigns and track ROI.' },
      { id: 'int-4', title: 'Payment Gateways', content: 'Accept payments via Stripe or PayPal directly through forms and funnels.' },
      { id: 'int-5', title: 'Webhooks & API', content: 'Use webhooks and our API for custom integrations with your existing systems.' },
    ]
  },
];

// Workspace-Specific Help Categories
const POD_CATEGORIES = [
  {
    id: 'pod-basics',
    title: 'Workspace Basics',
    icon: Rocket,
    color: 'from-cyan-500 to-primary',
    description: 'Learn how the Workspace works',
    articles: [
      { id: 'pod-1', title: 'What is AI Horse-Powered Systems?', content: 'AI Horse-Powered Systems is the AI Business Operating System. Seven integrated platforms — Workspace, AGI Workhorse, CRM, POD, Directory, Ad Manager, and Bot — that deploy AI assistants, agents, and employees to run your marketing, operations, and customer support. Access Done-For-You services, Done-With-You expert sessions, and DIY tools to grow your business.' },
      { id: 'pod-2', title: 'Understanding Co-Op Funds', content: 'Co-Op Funds are funds you can use for any Workspace service. You receive $120,000 in Co-Op Funds with a small management fee (4% standard or 5% Easy Pay).' },
      { id: 'pod-3', title: 'Placing Your First Order', content: 'Browse the marketplace, select a service, describe your requirements, and submit. Our team handles the rest!' },
      { id: 'pod-4', title: 'Tracking Orders & Messages', content: 'View all your orders from the Orders page. Each order has a dedicated message thread for updates and revisions.' },
      { id: 'pod-5', title: 'DIY Tools Overview', content: 'Access 3000+ AI-powered tools to create ads, videos, images, copy, and more on your own.' },
    ]
  },
  {
    id: 'cinematic-ads',
    title: 'Cinematic Video Ads',
    icon: PlayCircle,
    color: 'from-purple-500 to-pink-500',
    description: 'Create stunning video ads',
    articles: [
      { id: 'cin-1', title: 'Cinematic Ad System Overview', content: 'Create complete video ad campaigns with landing pages, ad copy, and automated nurture sequences that publish everywhere.' },
      { id: 'cin-2', title: 'Using the AI Ad Generator', content: 'Generate ad images with Nano Banana, create videos with Sora 2, add logos, background music, and voice-overs.' },
      { id: 'cin-3', title: 'Launch Vector - AI Film Creation', content: 'Use Hype Jet 8.2 to create scripts, storyboards, and AI images for your short films.' },
      { id: 'cin-4', title: 'Publishing to Multiple Channels', content: 'Deploy your cinematic ads to Meta, Google, TikTok, TV, Radio, and Digital Billboards.' },
    ]
  },
  {
    id: 'affiliate',
    title: 'Affiliate Program',
    icon: TrendingUp,
    color: 'from-green-500 to-emerald-500',
    description: 'Earn with referrals',
    articles: [
      { id: 'aff-1', title: 'How the Affiliate Program Works', content: 'Earn commissions by referring businesses to the Workspace. Share your unique affiliate link and track your earnings.' },
      { id: 'aff-2', title: 'Commission Structure', content: 'Earn up to 20% recurring commission on referrals. Higher tiers unlock better rates.' },
      { id: 'aff-3', title: 'Marketing Materials', content: 'Access banners, email templates, and social posts to promote your affiliate link.' },
      { id: 'aff-4', title: 'Tracking & Payouts', content: 'View your referrals, clicks, and commissions in the Affiliate Dashboard. Payouts are processed monthly.' },
    ]
  },
];

// Changelog/Updates Data
const CHANGELOG_ENTRIES = [
  {
    date: 'January 2026',
    updates: [
      { type: 'new', title: 'Launch Vector AI Film Creator', description: 'Create complete short films with AI-generated scripts, images, and storyboards using Hype Jet 8.2.' },
      { type: 'new', title: 'Video Generation with Sora 2', description: 'Generate stunning video ads directly in the DIY Tools with our new Sora 2 integration.' },
      { type: 'improvement', title: 'Enhanced Ad Generator', description: 'Added logo overlay, background music, and voice-over options to the AI Ad Generator.' },
      { type: 'fix', title: 'Order Messaging Fix', description: 'Resolved issues with sending messages in order threads.' },
    ]
  },
  {
    date: 'December 2025',
    updates: [
      { type: 'new', title: 'Cinematic Video Ad System', description: 'Complete video ad production system with landing pages and automated nurture sequences.' },
      { type: 'new', title: 'AI Horse-Powered Dashboard', description: 'New dashboard design with quick links and progress tracking.' },
      { type: 'improvement', title: 'Landing Page Redesign', description: 'Updated landing page with compelling storytelling and feature showcase.' },
    ]
  },
  {
    date: 'November 2025',
    updates: [
      { type: 'new', title: 'Directory & Lead Generation', description: 'Public business directory with contact forms and lead capture.' },
      { type: 'new', title: 'Affiliate Dashboard', description: 'Track referrals, commissions, and payouts in your affiliate dashboard.' },
      { type: 'improvement', title: 'CRM Integration', description: 'Enhanced CRM connection with better sync options.' },
    ]
  },
];

export default function KnowledgeBasePage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('crm');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  
  // Chat State
  const [chatMessages, setChatMessages] = useState([
    { type: 'ai', text: "Hello! I'm Athena, your AI Executive Assistant. Ask me anything about the CRM, POD, Workspace, or how to grow your business." }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showVoiceChat, setShowVoiceChat] = useState(false);
  const chatEndRef = useRef(null);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadData = async () => {
    try {
      const articlesRes = await api.getArticles();
      setArticles(articlesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendChatMessage = async (messageData) => {
    const userMessage = typeof messageData === 'string' ? messageData : messageData?.content;
    if (!userMessage?.trim() || chatLoading) return;

    setChatInput('');
    const userMsgObj = { 
      type: 'user', 
      text: userMessage,
      attachments: messageData?.attachments || null
    };
    setChatMessages(prev => [...prev, userMsgObj]);
    setChatLoading(true);

    try {
      let contextMessage = userMessage;
      if (messageData?.attachments?.length > 0) {
        const fileNames = messageData.attachments.map(a => a.name).join(', ');
        contextMessage += ` [User attached files: ${fileNames}]`;
      }
      
      const response = await api.sendChatMessage({ 
        message: contextMessage,
        session_id: sessionId
      });
      
      setSessionId(response.data.session_id);
      setChatMessages(prev => [...prev, { 
        type: 'ai', 
        text: response.data.response,
        citations: response.data.citations
      }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        type: 'ai', 
        text: "I apologize, I'm having trouble connecting right now. Please try again or browse the help articles below."
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const currentCategories = activeTab === 'crm' ? CRM_CATEGORIES : POD_CATEGORIES;
  
  const filteredCategories = currentCategories.filter(cat => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return cat.title.toLowerCase().includes(searchLower) ||
           cat.description.toLowerCase().includes(searchLower) ||
           cat.articles.some(a => a.title.toLowerCase().includes(searchLower));
  });

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            Knowledge Base & AI Support
          </h1>
          <p className="text-gray-400">Find answers, learn features, and get instant AI assistance</p>
        </motion.div>

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-6"
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {QUICK_LINKS.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className="glass rounded-xl p-4 flex items-center gap-3 hover:border-primary/30 transition-all group"
                >
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${link.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-medium text-white group-hover:text-primary transition-colors">{link.label}</span>
                </Link>
              );
            })}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Main Content Area (3 cols) */}
          <div className="lg:col-span-3 space-y-6">
            {/* Search */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass rounded-xl p-4"
            >
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type="text"
                  placeholder="Search help articles..."
                  className="pl-10 input-dark"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="kb-search"
                />
              </div>
            </motion.div>

            {/* Tabs for CRM vs Workspace */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid grid-cols-3 mb-6">
                <TabsTrigger value="crm" className="flex items-center gap-2">
                  <Database className="w-4 h-4" />
                  CRM Help
                </TabsTrigger>
                <TabsTrigger value="pod" className="flex items-center gap-2">
                  <Rocket className="w-4 h-4" />
                  Workspace
                </TabsTrigger>
                <TabsTrigger value="changelog" className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Updates
                </TabsTrigger>
              </TabsList>

              {/* CRM & Workspace Content */}
              <TabsContent value="crm" className="mt-0">
                {selectedArticle ? (
                  <ArticleDetail article={selectedArticle} onBack={() => setSelectedArticle(null)} />
                ) : selectedCategory ? (
                  <CategoryDetail 
                    category={selectedCategory} 
                    onBack={() => setSelectedCategory(null)}
                    onSelectArticle={setSelectedArticle}
                  />
                ) : (
                  <CategoriesGrid 
                    categories={filteredCategories} 
                    onSelectCategory={setSelectedCategory}
                  />
                )}
              </TabsContent>

              <TabsContent value="pod" className="mt-0">
                {selectedArticle ? (
                  <ArticleDetail article={selectedArticle} onBack={() => setSelectedArticle(null)} />
                ) : selectedCategory ? (
                  <CategoryDetail 
                    category={selectedCategory} 
                    onBack={() => setSelectedCategory(null)}
                    onSelectArticle={setSelectedArticle}
                  />
                ) : (
                  <CategoriesGrid 
                    categories={filteredCategories} 
                    onSelectCategory={setSelectedCategory}
                  />
                )}
              </TabsContent>

              {/* Changelog Tab */}
              <TabsContent value="changelog" className="mt-0">
                <ChangelogView />
              </TabsContent>
            </Tabs>
          </div>

          {/* Athena AI Chat Section (2 cols) */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2"
          >
            <div className="glass rounded-2xl overflow-hidden sticky top-24">
              {/* Athena Image with gradient fade */}
              <div className="relative h-40 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_aipowered-5/artifacts/7tfm7q8x_ChatGPT%20Image%20Jan%209%2C%202026%2C%2005_02_53%20PM.png"
                  alt="Athena - AI Executive Assistant"
                  className="w-full h-56 object-cover object-top"
                  style={{
                    maskImage: 'linear-gradient(to bottom, black 40%, transparent 100%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, black 40%, transparent 100%)'
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/60 to-transparent" />
              </div>
              
              {/* Content */}
              <div className="relative -mt-12 px-6 pb-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center border border-amber-500/30">
                    <Sparkles className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-white">Athena</h2>
                    <p className="text-xs text-amber-400">AI Executive Assistant</p>
                  </div>
                  <Button 
                    onClick={() => setShowVoiceChat(true)}
                    className="ml-auto px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white border border-white/20 h-auto"
                    data-testid="athena-kb-voice-btn"
                  >
                    <Phone className="w-4 h-4" />
                  </Button>
                </div>

                {/* Chat Messages */}
                <div className="h-[320px] overflow-y-auto space-y-3 mb-4 pr-2">
                  {chatMessages.map((msg, i) => (
                    <div
                      key={i}
                      className={cn(
                        "flex",
                        msg.type === 'user' ? "justify-end" : "justify-start"
                      )}
                    >
                      <div className={cn(
                        "max-w-[85%] rounded-2xl px-4 py-3",
                        msg.type === 'user'
                          ? "bg-amber-500 text-black"
                          : "bg-black/30 text-gray-200 border border-amber-500/20"
                      )}>
                        {msg.type === 'user' ? (
                          <p className="text-sm">{msg.text}</p>
                        ) : (
                          <div 
                            className="text-sm ai-response"
                            dangerouslySetInnerHTML={{ __html: formatAIResponse(msg.text) }}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-black/30 border border-amber-500/20 rounded-2xl px-4 py-3">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Chat Input */}
                <EnhancedChatInput
                  onSend={sendChatMessage}
                  placeholder="Ask Athena anything..."
                  disabled={chatLoading}
                  loading={chatLoading}
                  buttonColor="from-amber-500 to-orange-500"
                  buttonTextColor="text-black"
                  className="mt-2"
                />

                {/* Quick Actions */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-xs text-gray-500 mb-2">Popular questions:</p>
                  <div className="flex flex-wrap gap-2">
                    {['How do automations work?', 'Set up a calendar', 'Create a funnel', 'Track my orders'].map((q) => (
                      <button
                        key={q}
                        onClick={() => sendChatMessage({ content: q })}
                        className="px-3 py-1 rounded-full bg-white/5 text-xs text-gray-400 hover:bg-amber-500/20 hover:text-amber-400 transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Athena Voice Chat Modal */}
      <AthenaVoiceChat 
        isOpen={showVoiceChat} 
        onClose={() => setShowVoiceChat(false)} 
      />
    </DashboardLayout>
  );
}

// Categories Grid Component
function CategoriesGrid({ categories, onSelectCategory }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="grid sm:grid-cols-2 gap-4"
    >
      {categories.map((category, i) => {
        const Icon = category.icon;
        return (
          <motion.button
            key={category.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            onClick={() => onSelectCategory(category)}
            className="glass rounded-xl p-5 text-left hover:border-primary/30 transition-all group"
          >
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-white mb-1 group-hover:text-primary transition-colors">
              {category.title}
            </h3>
            <p className="text-sm text-gray-400">{category.description}</p>
            <p className="text-xs text-gray-500 mt-2">{category.articles.length} articles</p>
          </motion.button>
        );
      })}
    </motion.div>
  );
}

// Category Detail Component
function CategoryDetail({ category, onBack, onSelectArticle }) {
  const Icon = category.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <button
        onClick={onBack}
        className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
      >
        ← Back to categories
      </button>
      
      <div className="glass rounded-xl p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center`}>
            <Icon className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-white">{category.title}</h2>
            <p className="text-gray-400">{category.description}</p>
          </div>
        </div>
        
        <div className="space-y-3">
          {category.articles.map((article, i) => (
            <motion.button
              key={article.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => onSelectArticle(article)}
              className="w-full text-left p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-gray-500 group-hover:text-primary transition-colors" />
                <span className="text-white group-hover:text-primary transition-colors">{article.title}</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-primary transition-colors" />
            </motion.button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// Article Detail Component
function ArticleDetail({ article, onBack }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-xl p-6"
    >
      <button
        onClick={onBack}
        className="text-sm text-gray-400 hover:text-white mb-4 flex items-center gap-1"
      >
        ← Back
      </button>
      
      <h2 className="font-heading text-2xl font-bold text-white mb-4">
        {article.title}
      </h2>
      
      <div className="prose prose-invert max-w-none">
        <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">{article.content}</p>
      </div>
      
      <div className="mt-6 pt-6 border-t border-white/10">
        <p className="text-sm text-gray-500 mb-3">Was this helpful?</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-2">
            <CheckCircle className="w-4 h-4" /> Yes
          </Button>
          <Button variant="outline" size="sm" className="gap-2">
            <HelpCircle className="w-4 h-4" /> Need more help
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

// Changelog View Component
function ChangelogView() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      <div className="glass rounded-xl p-6">
        <h2 className="font-heading text-xl font-bold text-white mb-2">Product Updates & Changelog</h2>
        <p className="text-gray-400 text-sm">Stay up to date with the latest features and improvements</p>
      </div>
      
      {CHANGELOG_ENTRIES.map((entry, i) => (
        <motion.div
          key={entry.date}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1 }}
          className="glass rounded-xl p-6"
        >
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            {entry.date}
          </h3>
          
          <div className="space-y-4">
            {entry.updates.map((update, j) => (
              <div key={j} className="flex gap-3">
                <span className={cn(
                  "px-2 py-0.5 rounded text-xs font-medium shrink-0 mt-0.5",
                  update.type === 'new' && "bg-green-500/20 text-green-400",
                  update.type === 'improvement' && "bg-blue-500/20 text-blue-400",
                  update.type === 'fix' && "bg-orange-500/20 text-orange-400"
                )}>
                  {update.type === 'new' ? 'NEW' : update.type === 'improvement' ? 'IMPROVED' : 'FIXED'}
                </span>
                <div>
                  <h4 className="font-medium text-white">{update.title}</h4>
                  <p className="text-sm text-gray-400">{update.description}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}
